import { useState, useCallback } from 'react';
import { CartItem } from '../QuickSaleScreen';

interface UseQuickSaleDiscountProps {
  cart: CartItem[];
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  showAlertModal: (title: string, message: string) => void;
  focusSearchBar?: () => void; // ✅ YENİ: Barkod arama bölümüne fokuslanma
}

interface UseQuickSaleDiscountReturn {
  // State
  isDiscountModalOpen: boolean;
  discountInputPercentage: string;
  discountInputAmount: string;
  lastAppliedDiscountAmount: number;
  appliedDiscountType: 'percentage' | 'amount' | null;
  
  // Actions
  setIsDiscountModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDiscountInputPercentage: React.Dispatch<React.SetStateAction<string>>;
  setDiscountInputAmount: React.Dispatch<React.SetStateAction<string>>;
  handleOpenDiscountModal: () => void;
  handleCloseDiscountModal: () => void;
  handleApplyDiscount: () => void;
  /** Hızlı yüzde indirimi (%10/%20 butonları): input'a yazmadan doğrudan uygular ve modalı kapatır. */
  handleApplyQuickDiscount: (percent: number) => void;
  handleRemoveDiscount: () => void;
}

export const useQuickSaleDiscount = ({
  cart,
  setCart,
  showAlertModal,
  focusSearchBar, // ✅ YENİ
}: UseQuickSaleDiscountProps): UseQuickSaleDiscountReturn => {
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [discountInputPercentage, setDiscountInputPercentage] = useState('');
  const [discountInputAmount, setDiscountInputAmount] = useState('');
  const [lastAppliedDiscountAmount, setLastAppliedDiscountAmount] = useState<number>(0);
  const [appliedDiscountType, setAppliedDiscountType] = useState<'percentage' | 'amount' | null>(null);

  // İndirim modalını açma
  const handleOpenDiscountModal = useCallback(() => {
    if (cart.length === 0) {
      showAlertModal('Uyarı', 'İndirim uygulamak için sepette ürün olmalıdır.');
      return;
    }
    setIsDiscountModalOpen(true);
  }, [cart.length, showAlertModal]);

  // İndirim modalını kapatma
  const handleCloseDiscountModal = useCallback(() => {
    setIsDiscountModalOpen(false);
    setDiscountInputPercentage('');
    setDiscountInputAmount('');
    
    // ✅ YENİ: Modal kapandığında barkod arama bölümüne fokuslan
    if (focusSearchBar) {
      setTimeout(() => {
        focusSearchBar();
      }, 100);
    }
  }, [focusSearchBar]);

  // Yüzde indirimini sepete uygular (KDV DAHİL birim fiyat üzerinden).
  // Hem "Uygula" butonundaki yüzde girişi hem de hızlı %10/%20 butonları bunu kullanır.
  // originalPrice baz alındığı için mevcut bir indirimin üzerine binmez, onu değiştirir.
  const applyPercentageToCart = useCallback((discountPercent: number) => {
    const updatedCart = cart.map((item) => {
      const originalPriceToUse = item.originalPrice !== undefined ? item.originalPrice : item.price;
      const vatRate = (item.vatRate || 0) / 100; // KDV oranı (ondalık)
      
      // ⚠️ TERMİNOLOJİ: 'included' = KDV dahil fiyat, 'excluded' = KDV hariç fiyat
      const isVatIncluded = item.vatStatus === 'included';
      
      // 1️⃣ ADIM: KDV DAHİL fiyatı hesapla
      const priceWithVat = isVatIncluded 
        ? originalPriceToUse // Zaten KDV dahil
        : originalPriceToUse * (1 + vatRate); // KDV ekle
      
      // 2️⃣ ADIM: İndirimi KDV DAHİL fiyata uygula
      const discountedPriceWithVat = priceWithVat * (1 - discountPercent / 100);
      
      // 3️⃣ ADIM: Fiyatı geri hesapla (aynı formatta sakla)
      const newPrice = isVatIncluded 
        ? discountedPriceWithVat // KDV dahil olarak sakla
        : discountedPriceWithVat / (1 + vatRate); // KDV hariç olarak sakla
      
      return {
        ...item,
        price: newPrice,
        originalPrice: originalPriceToUse,
      };
    });

    setCart(updatedCart);
    
    // İndirim tutarını hesapla (KDV dahil toplam üzerinden)
    const totalDiscount = cart.reduce((sum, item) => {
      const originalPriceToUse = item.originalPrice !== undefined ? item.originalPrice : item.price;
      const vatRate = (item.vatRate || 0) / 100;
      const isVatIncluded = item.vatStatus === 'included';
      const priceWithVat = isVatIncluded 
        ? originalPriceToUse 
        : originalPriceToUse * (1 + vatRate);
      const discountPerItem = (priceWithVat * discountPercent / 100) * item.quantity;
      return sum + discountPerItem;
    }, 0);

    setLastAppliedDiscountAmount(totalDiscount);
    setAppliedDiscountType('percentage');
  }, [cart, setCart]);

  // İndirim uygulama
  const handleApplyDiscount = useCallback(() => {
    if (cart.length === 0) {
      showAlertModal('Uyarı', 'İndirim uygulamak için sepette ürün olmalıdır.');
      setIsDiscountModalOpen(false);
      return;
    }

    // Eğer bir indirim daha önce uygulanmışsa, önce geri al
    if (lastAppliedDiscountAmount > 0) {
      handleRemoveDiscount();
    }

    // Yüzde indirimi mi yoksa tutar indirimi mi?
    if (discountInputPercentage.trim() !== '') {
      // ✅ Yüzde indirimi - KDV DAHİL birim fiyata uygulanır
      const discountPercent = parseFloat(discountInputPercentage);
      
      if (isNaN(discountPercent) || discountPercent < 0 || discountPercent > 100) {
        showAlertModal('Hata', 'Geçersiz indirim yüzdesi. Lütfen 0 ile 100 arasında bir değer girin.');
        return;
      }

      applyPercentageToCart(discountPercent);
      
      // ✅ Başarı mesajı kaldırıldı - Modal kapanırken kullanıcı zaten indirimi görüyor
      
    } else if (discountInputAmount.trim() !== '') {
      // ✅ YENİ: Tutar indirimi - KDV DAHİL toplam üzerinden orantılı dağıtım
      const discountAmountValue = parseFloat(discountInputAmount);
      
      if (isNaN(discountAmountValue) || discountAmountValue < 0) {
        showAlertModal('Hata', 'Geçersiz indirim tutarı. Lütfen geçerli bir tutar girin.');
        return;
      }

      // Sepet toplamını hesapla (KDV DAHİL)
      const cartSubtotalWithVat = cart.reduce((sum, item) => {
        const vatRate = (item.vatRate || 0) / 100;
        // ⚠️ TERMİNOLOJİ: 'included' = KDV dahil fiyat
        const isVatIncluded = item.vatStatus === 'included';
        const priceWithVat = isVatIncluded 
          ? item.price 
          : item.price * (1 + vatRate);
        return sum + (priceWithVat * item.quantity);
      }, 0);
      
      if (discountAmountValue > cartSubtotalWithVat) {
        showAlertModal('Hata', 'İndirim tutarı sepet toplamından (KDV dahil) büyük olamaz.');
        return;
      }

      // Her bir ürüne orantılı olarak indirim uygula (KDV dahil fiyat üzerinden)
      const discountRatio = discountAmountValue / cartSubtotalWithVat;
      
      const updatedCart = cart.map((item) => {
        const originalPriceToUse = item.originalPrice !== undefined ? item.originalPrice : item.price;
        const vatRate = (item.vatRate || 0) / 100;
        
        // ⚠️ TERMİNOLOJİ: 'included' = KDV dahil fiyat
        const isVatIncluded = item.vatStatus === 'included';
        
        // 1️⃣ ADIM: KDV DAHİL fiyatı hesapla
        const priceWithVat = isVatIncluded 
          ? originalPriceToUse // Zaten KDV dahil
          : originalPriceToUse * (1 + vatRate); // KDV ekle
        
        // 2️⃣ ADIM: İndirimi KDV DAHİL fiyata uygula
        const discountedPriceWithVat = priceWithVat * (1 - discountRatio);
        
        // 3️⃣ ADIM: Fiyatı geri hesapla (aynı formatta sakla)
        const newPrice = isVatIncluded 
          ? discountedPriceWithVat // KDV dahil olarak sakla
          : discountedPriceWithVat / (1 + vatRate); // KDV hariç olarak sakla
        
        return {
          ...item,
          price: newPrice,
          originalPrice: originalPriceToUse,
        };
      });

      setCart(updatedCart);
      setLastAppliedDiscountAmount(discountAmountValue);
      setAppliedDiscountType('amount');
      
      // ✅ Başarı mesajı kaldırıldı - Modal kapanırken kullanıcı zaten indirimi görüyor
      
    } else {
      showAlertModal('Uyarı', 'Lütfen bir indirim yüzdesi veya tutarı girin.');
      return;
    }

    // Modalı kapat ve input'ları temizle
    handleCloseDiscountModal();
  }, [
    cart,
    discountInputPercentage,
    discountInputAmount,
    lastAppliedDiscountAmount,
    setCart,
    showAlertModal,
    handleCloseDiscountModal,
    applyPercentageToCart,
  ]);

  // Hızlı yüzde indirimi (%10/%20 butonları): input'a yazıp "Uygula"ya basmakla
  // birebir aynı sonucu üretir, ekstra tıklama gerektirmeden modalı kapatır.
  const handleApplyQuickDiscount = useCallback((percent: number) => {
    if (cart.length === 0) {
      showAlertModal('Uyarı', 'İndirim uygulamak için sepette ürün olmalıdır.');
      setIsDiscountModalOpen(false);
      return;
    }

    // Eğer bir indirim daha önce uygulanmışsa, önce geri al (Uygula akışıyla aynı davranış)
    if (lastAppliedDiscountAmount > 0) {
      handleRemoveDiscount();
    }

    applyPercentageToCart(percent);
    handleCloseDiscountModal();
  }, [
    cart.length,
    lastAppliedDiscountAmount,
    showAlertModal,
    applyPercentageToCart,
    handleCloseDiscountModal,
  ]);

  // İndirimi kaldırma
  const handleRemoveDiscount = useCallback(() => {
    if (lastAppliedDiscountAmount === 0) {
      return; // Zaten indirim yok
    }

    // Orijinal fiyatları geri yükle
    const updatedCart = cart.map((item) => ({
      ...item,
      price: item.originalPrice !== undefined ? item.originalPrice : item.price,
      originalPrice: undefined,
    }));

    setCart(updatedCart);
    setLastAppliedDiscountAmount(0);
    setAppliedDiscountType(null);
    setDiscountInputPercentage('');
    setDiscountInputAmount('');
  }, [cart, lastAppliedDiscountAmount, setCart]);

  return {
    isDiscountModalOpen,
    discountInputPercentage,
    discountInputAmount,
    lastAppliedDiscountAmount,
    appliedDiscountType,
    setIsDiscountModalOpen,
    setDiscountInputPercentage,
    setDiscountInputAmount,
    handleOpenDiscountModal,
    handleCloseDiscountModal,
    handleApplyDiscount,
    handleApplyQuickDiscount,
    handleRemoveDiscount,
  };
};

